import { Alert, Col, Row, Select, Spin, Tag, Tooltip } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

const ServiceDetailPanel = ({
  prestation,
  availableExams,
  loading,
  onRemovePrestation,
  onReportTypeChange,
  onSearchAdditionals,
  onAddAdditional,
  onRemoveExam,
}) => {
  const { t } = useTranslation();
  const isInvoiced = prestation?.invoiced === true;

  if (!prestation) {
    return (
      <div className="service-detail-panel service-detail-panel--empty">
        <p className="color-secondary text-center mt-50">
          Seleccione una prestación del listado para ver su detalle
        </p>
      </div>
    );
  }

  const isBattery = prestation.type !== 'Exam';

  const visibleExams = isBattery
    ? [
      ...prestation.additionals.map(e => ({ ...e, isAdditional: true })),
      ...prestation.battery_exams
        .filter(e => !prestation.removedExamIds.includes(e.id))
        .map(e => ({
          ...e,
          isAdditional: prestation.conditionalExams?.includes(e.id) ? true : e.isAdditional,
        })),
    ]
    : [{ id: prestation.id, name: prestation.name, sale_amount: prestation.sale_amount }];

  const reportTypeLabels = t('admissionPage.reportTypeLabels', { returnObjects: true });
  const formatOptions = Object.entries(reportTypeLabels).map(([value, label]) => ({
    value,
    label,
  }));

  const alreadyAdditionalIds = prestation.additionals.map(additional => additional.id);
  const batteryExamIds = prestation.battery_exams
    .filter(exam => !prestation.removedExamIds.includes(exam.id))
    .map(exam => exam.id);

  return (
    <div className="service-detail-panel">
      <Row align="top" justify="space-between" wrap={false} className="mb-20">
        <Col flex="auto" className="pr-15">
          <h2 className="mb-0">Seleccionada: {prestation.name}</h2>
        </Col>
        <Col flex="none">
          <Tooltip title="Elimina prestación seleccionada">
            <span
              className="icon-system-delete delete-prestation-icon"
              onClick={() => onRemovePrestation(`${prestation.id}-${prestation.type}`)}
            />
          </Tooltip>
        </Col>
      </Row>

      {prestation.type !== 'Exam' && (
        <Row gutter={15} className="mb-20">
          <Col lg={12} xs={24}>
            <p className="mb-5 fs-12">Formato principal</p>
            <Select
              className="cmt-select w-100"
              value={prestation.report_type}
              onChange={onReportTypeChange}
              placeholder="Seleccione formato"
            >
              {formatOptions?.length > 0 && formatOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
            {prestation.report_type?.includes('v2') && (
              <Alert
                type="info"
                showIcon
                className="alert-report-type mt-10"
                message="Estás usando un formato de informe v2"
                description="Recuerda incluir únicamente exámenes nuevos para que el informe se genere correctamente."
              />
            )}
          </Col>
          <Col lg={12} xs={24}>
            <p className="mb-5 fs-12">Adicionales incorporados a la batería</p>
            <Tooltip
              title={isInvoiced ? 'No se permiten modificaciones en una batería facturada' : ''}
            >
              <Spin spinning={loading.exam} className="cmt-spin">
                <Select
                  className="cmt-select w-100"
                  showSearch
                  disabled={isInvoiced}
                  filterOption={false}
                  placeholder="Seleccione examen adicional"
                  notFoundContent={null}
                  value={null}
                  onSearch={(val) => onSearchAdditionals(val)}
                  onSelect={(_, option) => {
                    onAddAdditional(option.examData);
                  }}
                >
                  {availableExams?.length > 0 && availableExams.map(exam => {
                    const isAlreadyIncluded = alreadyAdditionalIds.includes(exam.id) || batteryExamIds.includes(exam.id);
                    return (
                      <Option
                        key={exam.id}
                        value={exam.id}
                        examData={exam}
                        disabled={isAlreadyIncluded}
                      >
                        <div className="service-select-item">
                          <span>
                            {exam.name}
                            {exam.department_id && (
                              <Tag
                                className={`fs-10 ml-5 ${exam.department_id === 1 ? 'tag-department-health' : 'tag-department-psycho'}`}
                              >
                                {exam.department_id === 1 ? 'Salud' : 'Psicología'}
                              </Tag>
                            )}
                          </span>
                          {isAlreadyIncluded && <CheckOutlined className="service-check-icon" />}
                        </div>
                      </Option>
                    );
                  })}
                </Select>
              </Spin>
            </Tooltip>
          </Col>
        </Row>
      )}

      <table className="exam-detail-table w-100 mb-10">
        <thead>
          <tr>
            <th className="text-left">Examen</th>
            <th className="text-right">Precio</th>
            {isBattery && <th />}
          </tr>
        </thead>
        <tbody>
          {visibleExams?.length > 0 && visibleExams.map((exam, index) => (
            <tr key={`${exam.id}-${index}`}>
              <td>
                <Row align="middle" gutter={8}>
                  <Col>
                    <span>{exam.name}</span>
                  </Col>
                  {exam.department_id && (
                    <Col>
                      <Tag
                        className={`fs-10 ${exam.department_id === 1 ? 'tag-department-health' : 'tag-department-psycho'}`}
                      >
                        {exam.department_id === 1 ? 'Salud' : 'Psicología'}
                      </Tag>
                    </Col>
                  )}
                  {exam.isAdditional && (
                    <Col>
                      <Tag color="orange" className="fs-10">Adicional</Tag>
                    </Col>
                  )}
                </Row>
              </td>
              <td className="text-right">
                <span className="price-badge">
                  {exam.sale_amount != null && exam.sale_amount !== '' ? `S/ ${parseFloat(exam.net_amount !== undefined ? exam.net_amount : exam.sale_amount).toFixed(2)}` : '—'}
                </span>
              </td>
              {isBattery && (
                <td className="text-center action-cell">
                  {!isInvoiced && (
                    <Tooltip title="Elimina examen">
                      <span
                        className="icon-system-delete delete-exam-icon"
                        onClick={() => onRemoveExam(exam.id, !!exam.isAdditional)}
                      />
                    </Tooltip>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="total-row">
            <td><strong>Precio total (sin IGV)</strong></td>
            <td className="text-right">
              <strong>S/ {(prestation.sale_amount != null && prestation.sale_amount !== '' ? parseFloat(prestation.sale_amount) : 0).toFixed(2)}</strong>
            </td>
            {isBattery && <td />}
          </tr>
        </tfoot>
      </table>

      <p className="disclaimer-text">
        El precio total final puede variar según los exámenes condicionales requeridos.
      </p>

      {prestation.price_fallback && (
        <p className="disclaimer-text">
          Precio base aplicado por ítems sin valor. Adicionales se suman al total.
        </p>
      )}
    </div>
  );
};

export default ServiceDetailPanel;
